import {fabric} from 'fabric';
import {useCallback} from 'react';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';
import {autoFitScale} from '~/common/utils/autoFitScale';

export const useFabricImage = (canvas) => {
  const centerAndScaleImage = useCallback(
    (image, originalObject, options = {}) => {
      if (!canvas || !image || !originalObject) return null;

      const {makeSelectable = true, resetRotation = true, clipPath = null} = options;

      const scale = autoFitScale(image, originalObject) || 1;
      const centerX = originalObject.left + (originalObject.width * originalObject.scaleX) / 2;
      const centerY = originalObject.top + (originalObject.height * originalObject.scaleY) / 2;

      image.scale(scale).set({
        left: centerX,
        top: centerY,
        originX: 'center',
        originY: 'center',
        centeredRotation: true,
        selectable: makeSelectable,
        hasControls: true,
        _originalObject: originalObject,
      });

      if (clipPath) {
        // eslint-disable-next-line no-param-reassign
        image.clipPath = clipPath;
      }

      if (resetRotation) {
        image.rotate(0);
      }

      return image;
    },
    [canvas]
  );

  const createImageContainer = useCallback(
    (originalObject, id) =>
      new fabric.Rect({
        left: originalObject.left,
        top: originalObject.top,
        id: `${id}-container`,
        width: originalObject.width,
        height: originalObject.height,
        scaleX: originalObject.scaleX,
        scaleY: originalObject.scaleY,
        fill: 'white',
        selectable: false,
        hasControls: false,
      }),
    []
  );

  const createImageClipPath = useCallback((originalObject) => {
    const centerX = originalObject.left + (originalObject.width * originalObject.scaleX) / 2;
    const centerY = originalObject.top + (originalObject.height * originalObject.scaleY) / 2;

    return new fabric.Rect({
      left: centerX,
      top: centerY,
      originX: 'center',
      originY: 'center',
      width: originalObject.width * originalObject.scaleX,
      height: originalObject.height * originalObject.scaleY,
      absolutePositioned: true,
    });
  }, []);

  const resetImage = useCallback(
    (image) => {
      if (!canvas || !image || !image._originalObject) return undefined;

      return centerAndScaleImage(image, image._originalObject, {
        fireEvents: ['object:modified', {name: 'object:reseted:uyo', data: {scale: 1}}],
      });
    },
    [canvas, centerAndScaleImage]
  );

  const findUYOImageById = useCallback(
    (id) => {
      if (!canvas) return null;
      return canvas
        .getObjects()
        .find(
          (obj) =>
            (obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id) ||
            (obj.id || deriveIdFromCanvasObject(obj)) === `${id}-container`
        );
    },
    [canvas]
  );

  const sendRectsToBack = useCallback(() => {
    if (!canvas) return;

    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'rect') {
        canvas.sendToBack(obj);
      }
    });
  }, [canvas]);

  return {
    centerAndScaleImage,
    createImageContainer,
    createImageClipPath,
    resetImage,
    findUYOImageById,
    sendRectsToBack,
  };
};
